import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Zap } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  React.useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const u = await register(form.email, form.password, form.name || form.email.split("@")[0]);
        toast.success("Welcome to ContentOS");
        nav(u.onboarded ? "/app" : "/onboarding");
      } else {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name}`);
        nav(u.onboarded ? "/app" : "/onboarding");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-foreground">
      <div className="hidden md:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
            <Zap className="h-4 w-4" strokeWidth={2.5}/>
          </div>
          <span className="font-display font-black text-xl tracking-tight">ContentOS</span>
        </Link>
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">The promise</div>
          <div className="mt-4 font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
            Give us one hour. We'll learn your business like a marketing employee who's worked for you for a year.
          </div>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            {[["6", "AI employees"], ["7", "Posts / week"], ["∞", "Smarter"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display font-black text-3xl">{n}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-muted-foreground font-mono relative">© 2026 · Post smarter, not more.</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
            {mode === "register" ? "Create account" : "Welcome back"}
          </div>
          <h1 className="mt-2 font-display font-black text-3xl tracking-tight">
            {mode === "register" ? "Start your Brand Intelligence Profile" : "Sign in to ContentOS"}
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-widest">Your name</Label>
                <Input
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11" required
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11" required
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label className="text-xs uppercase tracking-widest">Password</Label>
                {mode === "login" && (
                  <Link
                    to="/auth/forgot"
                    data-testid="link-forgot-password"
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                  >
                    Forgot?
                  </Link>
                )}
              </div>
              <Input
                data-testid="input-password"
                type="password"
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11" required
              />
              {mode === "register" && <div className="mt-1 text-xs text-muted-foreground">At least 8 characters.</div>}
            </div>

            <Button data-testid="btn-submit-auth" type="submit" disabled={busy} className="w-full h-11 mt-2">
              {busy ? "Working…" : (mode === "register" ? "Create account" : "Sign in")}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground">
            {mode === "register" ? (
              <>Already have an account?{" "}
                <button data-testid="switch-to-login" onClick={() => setMode("login")} className="text-foreground font-semibold underline underline-offset-4">Sign in</button>
              </>
            ) : (
              <>New to ContentOS?{" "}
                <button data-testid="switch-to-register" onClick={() => setMode("register")} className="text-foreground font-semibold underline underline-offset-4">Create an account</button>
              </>
            )}
          </div>
          <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-4">
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/refunds" className="hover:text-foreground">Refunds</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
